import {subWeeks} from "date-fns";
import {useEffect, useRef, useState} from "react";
import ReportUtils from "../../../utils/report-utils.js";
import TrafficFlowRouteService from "../../../services/reports/traffic-flow-route-service.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Content from "./components/content.jsx";

const initialFilter = () => ({
    dtStart:  subWeeks(new Date(), 1),
    dtEnd: new Date(),
    period: { label: "Todos", value: "all" },
    hrStart: "00",
    hrEnd: "23",
    laneA: null,
    laneB: null,
});

const filterLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    period: "Período",
    hrStart: "Hora inicial",
    hrEnd: "Hora final",
    laneA: "Câmera A",
    laneB: "Câmera B",
}

export default function TrafficFlowRoute() {
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [fixedTop, setFixedTop] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [filters, setFilters] = useState(initialFilter);
    const [headerSelected, setHeaderSelected] = useState(-1);
    const printComponentRef = useRef(null);
    const buttonRef = useRef(null);
    const title = "Fluxo por rota";

    useEffect(() => {
        if (!reportData) return;

        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
            }, filterLabels)
        );
    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.validateOnlyDates(filters.dtStart, filters.dtEnd, 1, "month");

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setIsLoading(false);
                return;
            }

            if (!ReportUtils.validateHourOnly(filters.hrStart, filters.hrEnd, setIsLoading)) return;
            if (!ReportUtils.requiredFilterValidate(filters.laneA, "Câmera A", setIsLoading)) return;
            if (!ReportUtils.requiredFilterValidate(filters.laneB, "Câmera B", setIsLoading)) return;

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                period: filters.period?.value ?? "",
                hrStart: filters.hrStart ?? "",
                hrEnd: filters.hrEnd ?? "",
                laneA: filters.laneA?.value ?? "",
                laneB: filters.laneB?.value ?? "",
            }

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
            }, filterLabels, true);

            const response = await TrafficFlowRouteService.apply({ ...formattedFilters, filters: auditFilters});
            setReportData(response);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1) setCollapsed(true);

        }  catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setIsLoading(false);
        }
    }

    const clearSearch = () => {
        setFilters(initialFilter);
        setReportData(null);
    }

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }
        setCollapsed(prev => !prev);
    }

    useGlobalKey("Enter", apply);

    const hasData =
        !!reportData &&
        (
            (reportData.vehiclesTotal ?? 0) > 0 ||
            (reportData.dailyFlow?.length ?? 0) > 0 ||
            (reportData.weeklyFlow?.length ?? 0) > 0
        );
    const noDataValue = hasData ? 1 : 0;

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className="flex print:block print:w-full">
                <Template isLoading={isLoading}>
                    <Template.Header>
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            printable={true}
                            headerChange={true}
                            printComponentRef={printComponentRef}
                            reportData={reportData}
                            data={hasData}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                        />
                    </Template.Header>
                    <Template.Body className={`${collapsed ? "p-[0]" : ""}`}>
                        <Menu
                            buttonRef={buttonRef}
                            fixedTop={fixedTop}
                            collapsed={collapsed}
                            toggleFilters={toggleFilters}
                            clearSearch={clearSearch}
                            apply={apply}
                        >
                            <Filters filters={filters} setFilters={setFilters} />
                            <AutoDatetime setFilters={setFilters} />
                        </Menu>
                    </Template.Body>
                </Template>
                <Card className={`w-full h-fit mr-2  ${!reportData || !hasData ? "mt-[59px]" : "mt-[61px]"} print:!mt-[-15px] transform ${!reportData || !hasData ? "!bg-[#f3f3f4] !border-none !-translate-y-[5px]" : ""} ${collapsed ? "ml-2" : ""}`}>
                    <Card.Body className={`flex-1 ${!reportData || !hasData ? "p-[0]" : "px-[20px] py-[5px]"}`}>
                        <NotAppliedContent reportData={reportData} />
                        {reportData && !hasData && (
                        <NoDataContent reportData={reportData} data={noDataValue} />
                        )}
                        {reportData && hasData && (
                        <Content
                            reportData={reportData}
                            data={hasData}
                            entity={headerSelected}
                            title={title}
                            collapsed={collapsed}
                            printComponentRef={printComponentRef}
                            setIsLoading={setIsLoading}
                        />
                        )}
                    </Card.Body>
                </Card>
            </div>
        </SubScroll>
    )
}